import api from './api';

/**
 * Calendar Service - Wrapper for calendar and event-related API calls
 */
class CalendarService {
  // Calendar Management
  async getCalendars(filters = {}) {
    return await api.getCalendars(filters);
  }

  async getCalendar(calendarId) {
    return await api.getCalendar(calendarId);
  }

  async createCalendar(calendarData) {
    return await api.createCalendar(calendarData);
  }

  async updateCalendar(calendarId, calendarData) {
    return await api.updateCalendar(calendarId, calendarData);
  }

  async deleteCalendar(calendarId) {
    return await api.deleteCalendar(calendarId);
  }

  // Calendar Templates and Stats
  async getCalendarTemplates(year) {
    return await api.getCalendarTemplates(year);
  }

  async getCalendarStats() {
    return await api.getCalendarStats();
  }

  // Calendar Access and Availability
  async getAvailableCalendars() {
    try {
      console.log('[CalendarService] Fetching available calendars from /api/calendars/available');
      const response = await api.request('/api/calendars/available');
      console.log('[CalendarService] Response from /api/calendars/available:', response);

      // Handle different response formats
      if (Array.isArray(response)) {
        console.log('[CalendarService] Response is array, returning', response.length, 'calendars');
        return response;
      }
      if (response && response.calendars) {
        console.log('[CalendarService] Response has calendars property, returning', response.calendars.length, 'calendars');
        return response.calendars;
      }

      console.log('[CalendarService] No calendars in response, returning empty array');
      return [];
    } catch (error) {
      // Don't log error if it's just "no calendars found" (404)
      if (error.message && !error.message.includes('Calendar not found')) {
        console.error('[CalendarService] Error fetching available calendars:', error);
      } else {
        console.log('[CalendarService] No calendars found (404), returning empty array');
      }
      // Return empty array instead of throwing
      return [];
    }
  }

  async getCalendarSuggestions() {
    try {
      return await api.request('/api/calendars/suggestions');
    } catch (error) {
      console.error('Error fetching calendar suggestions:', error);
      return [];
    }
  }

  async getColorSuggestions(calendarType) {
    try {
      return await api.request(`/api/calendars/color-suggestions/${calendarType}`);
    } catch (error) {
      console.error('Error fetching color suggestions:', error);
      return { suggested_colors: ['#2196F3'], default_color: '#2196F3' };
    }
  }

  // Calendar Subscription
  async subscribeToCalendar(calendarId) {
    try {
      return await api.request(`/api/calendars/${calendarId}/subscribe`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error subscribing to calendar:', error);
      throw error;
    }
  }

  async unsubscribeFromCalendar(calendarId) {
    try {
      return await api.request(`/api/calendars/${calendarId}/subscribe`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error unsubscribing from calendar:', error);
      throw error;
    }
  }

  // Multi-Calendar View
  async getMultiCalendarView(calendarIds = [], filters = {}) {
    try {
      const params = new URLSearchParams();

      // Add calendar IDs (comma-separated)
      if (calendarIds && calendarIds.length > 0) {
        params.append('calendar_ids', calendarIds.join(','));
      }

      // Add view type
      if (filters.view_type) {
        params.append('view_type', filters.view_type);
      } else {
        params.append('view_type', 'agenda');
      }

      // Add time filter
      if (filters.time_filter) {
        params.append('time_filter', filters.time_filter);
      }

      // Add date range
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }

      return await api.request(`/api/calendars/multi-view?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching multi-calendar view:', error);
      return {
        calendars: [],
        events: [],
        total_events: 0,
        events_by_calendar: {}
      };
    }
  }

  // Event Management
  async getCalendarEvents(calendarId, filters = {}) {
    return await api.getCalendarEvents(calendarId, filters);
  }

  async getEvent(eventId) {
    return await api.getCalendarEvent(eventId);
  }

  async createEvent(calendarId, eventData) {
    return await api.createCalendarEvent(calendarId, eventData);
  }

  async updateEvent(eventId, eventData) {
    return await api.updateCalendarEvent(eventId, eventData);
  }

  async deleteEvent(eventId) {
    return await api.deleteCalendarEvent(eventId);
  }

  async respondToEvent(eventId, responseData) {
    try {
      return await api.request(`/api/calendars/events/${eventId}/rsvp`, {
        method: 'POST',
        body: JSON.stringify(responseData)
      });
    } catch (error) {
      console.error('Error responding to event:', error);
      throw error;
    }
  }

  async markAttendance(eventId, attendanceData, userId = null) {
    try {
      const params = userId ? `?user_id=${userId}` : '';
      return await api.request(`/api/calendars/events/${eventId}/attendance${params}`, {
        method: 'POST',
        body: JSON.stringify(attendanceData)
      });
    } catch (error) {
      console.error('Error marking attendance:', error);
      throw error;
    }
  }

  async getEventParticipations(eventId) {
    try {
      return await api.request(`/api/calendars/events/${eventId}/participations`);
    } catch (error) {
      console.error('Error fetching event participations:', error);
      return [];
    }
  }

  // My Events
  async getMyEvents(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.time_filter) {
        params.append('time_filter', filters.time_filter);
      }
      if (filters.calendar_id) {
        params.append('calendar_id', filters.calendar_id);
      }

      return await api.request(`/api/calendars/events/my-events?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching my events:', error);
      return { events: [], total: 0 };
    }
  }

  // Helper Methods
  /**
   * Group events by calendar
   */
  groupEventsByCalendar(events) {
    const grouped = {};

    events.forEach(event => {
      const calendarId = event.calendar_id;
      if (!grouped[calendarId]) {
        grouped[calendarId] = [];
      }
      grouped[calendarId].push(event);
    });

    return grouped;
  }

  /**
   * Get events for a specific date
   */
  getEventsForDate(events, date) {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === targetDate.getTime();
    });
  }

  /**
   * Sort events by start time
   */
  sortEventsByStartTime(events) {
    return [...events].sort((a, b) => {
      const aTime = new Date(a.start_time).getTime();
      const bTime = new Date(b.start_time).getTime();
      return aTime - bTime;
    });
  }

  /**
   * Filter events by date range
   */
  filterEventsByDateRange(events, startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return events.filter(event => {
      const eventStart = new Date(event.start_time);
      return eventStart >= start && eventStart <= end;
    });
  }

  /**
   * Get calendar by ID from list
   */
  getCalendarById(calendars, calendarId) {
    return calendars.find(cal => cal.id === calendarId);
  }

  /**
   * Get event count for calendar
   */
  getEventCountForCalendar(events, calendarId) {
    return events.filter(event => event.calendar_id === calendarId).length;
  }
}

export default new CalendarService();
